import React, { useState, useEffect } from 'react';
import {
  Container,
  Title,
  Paper,
  Stack,
  TextInput,
  Textarea,
  Button,
  Group,
  Switch,
  Divider,
  Grid,
  TagsInput,
  Loader,
  Tabs,
  Card,
  Text,
  Alert,
  Tooltip,
  ActionIcon,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import axios from 'axios';
import { useAppSelector } from '../../../store/hooks';
import { IconDeviceFloppy, IconCopy, IconInfoCircle, IconCheck } from '@tabler/icons-react';

interface SEOData {
  metaTitle: string;
  metaDescription: string;
  metaKeywords: string[];
  ogTitle: string;
  ogDescription: string;
  ogImage: string;
  twitterTitle: string;
  twitterDescription: string;
  twitterImage: string;
  canonicalUrl: string;
  robotsTxt: string;
  sitemapEnabled: boolean;
  analyticsId: string;
  googleVerification: string;
  bingVerification: string;
  structuredData: string;
  cacheTTL: number;
}

const pageNames = [
  { id: 'home', label: 'Home' },
  { id: 'products', label: 'Products' },
  { id: 'about', label: 'About Us' },
  { id: 'international', label: 'International' },
  { id: 'quality', label: 'Quality' },
  { id: 'certifications', label: 'Certifications' },
  { id: 'contact', label: 'Contact' },
];

const validateMetaTitle = (value: string) => {
  if (value.length < 30) return 'Title should be at least 30 characters';
  if (value.length > 60) return 'Title should not exceed 60 characters';
  return null;
};

const validateMetaDesc = (value: string) => {
  if (value.length < 50) return 'Description should be at least 50 characters';
  if (value.length > 160) return 'Description should not exceed 160 characters';
  return null;
};

export const SEOPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<string>('global');
  const [copied, setCopied] = useState<string | null>(null);
  const { token } = useAppSelector((state) => state.auth);
  const API_URL = import.meta.env.VITE_API_BASE_URL || 'https://thalvora-exports.onrender.com';

  // Global form
  const globalForm = useForm<SEOData>({
    initialValues: {
      metaTitle: '',
      metaDescription: '',
      metaKeywords: [],
      ogTitle: '',
      ogDescription: '',
      ogImage: '',
      twitterTitle: '',
      twitterDescription: '',
      twitterImage: '',
      canonicalUrl: '',
      robotsTxt: '',
      sitemapEnabled: true,
      analyticsId: '',
      googleVerification: '',
      bingVerification: '',
      structuredData: '',
      cacheTTL: 3600,
    },
    validate: {
      metaTitle: validateMetaTitle,
      metaDescription: validateMetaDesc,
    },
  });

  // Create forms for each page statically (at top level)
  const pageForms = pageNames.map((page) => ({
    pageId: page.id,
    form: useForm<SEOData>({
      initialValues: {
        metaTitle: '',
        metaDescription: '',
        metaKeywords: [],
        ogTitle: '',
        ogDescription: '',
        ogImage: '',
        twitterTitle: '',
        twitterDescription: '',
        twitterImage: '',
        canonicalUrl: '',
        robotsTxt: '',
        sitemapEnabled: true,
        analyticsId: '',
        googleVerification: '',
        bingVerification: '',
        structuredData: '',
        cacheTTL: 3600,
      },
      validate: {
        metaTitle: validateMetaTitle,
        metaDescription: validateMetaDesc,
      },
    }),
  }));

  // Map for quick access
  const pageFormMap = new Map(pageForms.map(p => [p.pageId, p.form]));

  useEffect(() => {
    fetchAllSettings();
  }, []);

  const fetchAllSettings = async () => {
    setInitialLoading(true);
    try {
      // Fetch global settings
      const globalRes = await axios.get(`${API_URL}/settings/seo/global`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      globalForm.setValues(globalRes.data);

      // Fetch settings for each page
      for (const page of pageNames) {
        const pageRes = await axios.get(`${API_URL}/settings/seo/page/${page.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const form = pageFormMap.get(page.id);
        if (form) {
          form.setValues(pageRes.data);
        }
      }
    } catch (error) {
      console.error('Error fetching SEO settings:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to load SEO settings',
        color: 'red',
      });
    } finally {
      setInitialLoading(false);
    }
  };

  const handleSaveGlobal = async (values: SEOData) => {
    setLoading(true);
    try {
      await axios.put(`${API_URL}/settings/seo/global`, values, {
        headers: { Authorization: `Bearer ${token}` }
      });
      notifications.show({
        title: 'Success',
        message: 'Global SEO settings updated',
        color: 'green',
      });
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to update settings',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSavePage = async (pageId: string, values: SEOData) => {
    setLoading(true);
    try {
      await axios.put(`${API_URL}/settings/seo/page/${pageId}`, values, {
        headers: { Authorization: `Bearer ${token}` }
      });
      notifications.show({
        title: 'Success',
        message: `SEO settings for ${pageNames.find(p => p.id === pageId)?.label} updated`,
        color: 'green',
      });
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to update settings',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  const SnippetPreview = ({ title, description, url }: { title: string; description: string; url?: string }) => (
    <Card withBorder p="sm" mb="md" bg="gray.0">
      <Text size="sm" c="blue" fw={500}>{url || 'https://thalvoraexports.com'}</Text>
      <Text fw={600} c="dark" size="md">{title || 'Your title here'}</Text>
      <Text size="sm" c="dimmed">{description || 'Your description here'}</Text>
    </Card>
  );

  const renderForm = (form: ReturnType<typeof useForm<SEOData>>, onSubmit: (values: SEOData) => void) => (
    <form onSubmit={form.onSubmit(onSubmit)}>
      <Stack>
        <Title order={4} mb="md">Meta Tags</Title>
        <Grid>
          <Grid.Col span={{ base: 12 }}>
            <TextInput
              label="Meta Title"
              placeholder="Enter meta title"
              description="Recommended: 50‑60 characters"
              error={form.errors.metaTitle}
              {...form.getInputProps('metaTitle')}
              required
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12 }}>
            <Textarea
              label="Meta Description"
              placeholder="Enter meta description"
              description="Recommended: 150‑160 characters"
              minRows={3}
              error={form.errors.metaDescription}
              {...form.getInputProps('metaDescription')}
              required
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12 }}>
            <TagsInput
              label="Meta Keywords"
              placeholder="Add keywords (press Enter)"
              data={[]}
              value={form.values.metaKeywords}
              onChange={(value) => form.setFieldValue('metaKeywords', value)}
              clearable
              splitChars={[',', ' ']}
            />
          </Grid.Col>
        </Grid>

        <Title order={5} mt="md">Google Preview</Title>
        <SnippetPreview
          title={form.values.metaTitle}
          description={form.values.metaDescription}
        />

        <Divider my="md" label="Open Graph (Facebook)" labelPosition="center" />
        <Grid>
          <Grid.Col span={{ base: 12 }}>
            <TextInput label="OG Title" {...form.getInputProps('ogTitle')} />
          </Grid.Col>
          <Grid.Col span={{ base: 12 }}>
            <Textarea label="OG Description" minRows={2} {...form.getInputProps('ogDescription')} />
          </Grid.Col>
          <Grid.Col span={{ base: 12 }}>
            <TextInput label="OG Image URL" placeholder="https://example.com/image.jpg" {...form.getInputProps('ogImage')} />
          </Grid.Col>
        </Grid>

        <Divider my="md" label="Twitter Cards" labelPosition="center" />
        <Grid>
          <Grid.Col span={{ base: 12 }}>
            <TextInput label="Twitter Title" {...form.getInputProps('twitterTitle')} />
          </Grid.Col>
          <Grid.Col span={{ base: 12 }}>
            <Textarea label="Twitter Description" minRows={2} {...form.getInputProps('twitterDescription')} />
          </Grid.Col>
          <Grid.Col span={{ base: 12 }}>
            <TextInput label="Twitter Image URL" {...form.getInputProps('twitterImage')} />
          </Grid.Col>
        </Grid>

        <Divider my="md" label="Technical SEO" labelPosition="center" />
        <Grid>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <TextInput label="Canonical URL" {...form.getInputProps('canonicalUrl')} />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <TextInput label="Google Analytics ID" {...form.getInputProps('analyticsId')} />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <TextInput
              label="Google Site Verification"
              rightSection={
                <Tooltip label="Copy to clipboard">
                  <ActionIcon onClick={() => copyToClipboard(form.values.googleVerification, 'google')}>
                    {copied === 'google' ? <IconCheck size={16} /> : <IconCopy size={16} />}
                  </ActionIcon>
                </Tooltip>
              }
              {...form.getInputProps('googleVerification')}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <TextInput
              label="Bing Site Verification"
              rightSection={
                <Tooltip label="Copy to clipboard">
                  <ActionIcon onClick={() => copyToClipboard(form.values.bingVerification, 'bing')}>
                    {copied === 'bing' ? <IconCheck size={16} /> : <IconCopy size={16} />}
                  </ActionIcon>
                </Tooltip>
              }
              {...form.getInputProps('bingVerification')}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12 }}>
            <Textarea label="Robots.txt" minRows={4} {...form.getInputProps('robotsTxt')} />
          </Grid.Col>
          <Grid.Col span={{ base: 12 }}>
            <Textarea
              label="Structured Data (JSON-LD)"
              placeholder='{"@context": "https://schema.org"}'
              minRows={4}
              {...form.getInputProps('structuredData')}
            />
          </Grid.Col>
        </Grid>

        <Divider my="md" label="Advanced Settings" labelPosition="center" />
        <Grid>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Switch
              label="Enable Sitemap"
              description="Automatically generate XML sitemap"
              {...form.getInputProps('sitemapEnabled', { type: 'checkbox' })}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <TextInput
              label="Cache TTL (seconds)"
              type="number"
              {...form.getInputProps('cacheTTL')}
            />
          </Grid.Col>
        </Grid>

        <Group justify="flex-end" mt="xl">
          <Button type="submit" color="exportGreen" loading={loading} leftSection={<IconDeviceFloppy size={16} />}>
            Save Settings
          </Button>
        </Group>
      </Stack>
    </form>
  );

  if (initialLoading) {
    return (
      <Container className="flex justify-center items-center h-64">
        <Loader size="lg" />
      </Container>
    );
  }

  return (
    <Container size="lg">
      <Title order={2} mb="lg">SEO Settings</Title>
      <Alert icon={<IconInfoCircle />} color="blue" mb="md">
        Optimize how your website appears in search engines and on social media.
      </Alert>

      <Tabs value={activeTab} onChange={(val) => setActiveTab(val || 'global')}>
        <Tabs.List>
          <Tabs.Tab value="global">🌐 Global</Tabs.Tab>
          {pageNames.map((page) => (
            <Tabs.Tab key={page.id} value={page.id}>
              {page.label}
            </Tabs.Tab>
          ))}
        </Tabs.List>

        <Tabs.Panel value="global" pt="xl">
          <Paper withBorder p="xl" radius="md">
            {renderForm(globalForm, handleSaveGlobal)}
          </Paper>
        </Tabs.Panel>

        {pageNames.map((page) => (
          <Tabs.Panel key={page.id} value={page.id} pt="xl">
            <Paper withBorder p="xl" radius="md">
              {renderForm(pageFormMap.get(page.id)!, (values) => handleSavePage(page.id, values))}
            </Paper>
          </Tabs.Panel>
        ))}
      </Tabs>
    </Container>
  );
};